import { Controller } from '@hotwired/stimulus'
import debounce from 'debounce'

interface ParcelInfoToAdd {
  parcels: Parcel[]
  processingEhubKey: string
  carrierRef: string
  firstArrivedAt: string
  toParam: string
}

interface Parcel {
  client: {
    id: number
    sender_instructions: unknown
  }
  client_ref: string
  id: number
  most_recent_delivery: {
    id: number
    shortcode: string
    sender_instructions: unknown
  }
  most_recent_ehub_scan_at: string
  ref: string
  type: 'bulk' | 'regular' | 'flat'
  waybill: {
    carrier_ref: string
    first_arrived_at: string
    human_state: string
    processing_ehub_key: string
    state: string
    to_param: string
  }
}

const keyMapping = {
  by_parcel: 'parcel_query',
  by_waybill: 'waybill_ref',
  by_shortcode_ehub: ['shortcode', 'processing_ehub']
}

function capitalizedText(text: string): string {
  return String(text).charAt(0).toUpperCase() + String(text).slice(1)
}

function updateLink(
  linkElement: HTMLAnchorElement,
  value: string,
  param: string | null = null,
  type: 'parcels' | 'waybills' = 'parcels'
): void {
  linkElement.href = `/${type}/${param ?? value}`
  ;(linkElement.firstChild as Text).textContent = value
}

function updateTime(parentElement: HTMLDivElement, timeString: string): void {
  const date = new Date(timeString)
  const timeElement = parentElement.querySelector('time')
  if (timeElement === null) return
  timeElement.innerText = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate()
  const tooltipElement = parentElement.querySelector('div.text-center')
  if (tooltipElement === null) return
  tooltipElement.innerHTML = date
    .toLocaleString('en-UK', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
    .replace(', ', '<br>')
}

// class

export default class extends Controller {
  static targets = [
    'serviceInput',
    'addParcelButton',
    'addParcelInfo',
    'addWaybillButton',
    'addWaybillInfo',
    'addShortcodeEhubButton',
    'addShortcodeEhubInfo'
  ]

  static values = {
    urls: Object,
    service: String
  }

  declare serviceInputTarget: HTMLInputElement
  declare addParcelButtonTarget: HTMLButtonElement
  declare addParcelInfoTarget: HTMLDivElement
  declare addWaybillButtonTarget: HTMLButtonElement
  declare addWaybillInfoTarget: HTMLDivElement
  declare addShortcodeEhubButtonTarget: HTMLButtonElement
  declare addShortcodeEhubInfoTarget: HTMLDivElement

  declare serviceValue: string
  declare urlsValue: {
    by_parcel: string
    by_waybill: string
    by_shortcode_ehub: string
  }

  form: HTMLFormElement | null = null
  parcelsToAdd: ParcelInfoToAdd | null = null
  repeatedIdsToAdd = new Set<number>()

  selectedShortcode = ''
  selectedEhub = ''

  connect(): void {
    this.form = this.element.querySelector('input')?.form ?? null
  }

  selectXParcels(e: Event): void {
    const target = e.currentTarget as HTMLInputElement
    const howMany = parseInt(target.value)
    if (Number.isNaN(howMany)) return

    const checkboxes = this.form?.querySelectorAll(
      'input[type="checkbox"]'
    ) as NodeListOf<HTMLInputElement>
    checkboxes.forEach((checkbox, index) => {
      checkbox.checked = false
      if (howMany > index) {
        checkbox.click()
      }
    })
  }

  sendBulkOperation(e: Event): void {
    e.preventDefault()
    const button = e.currentTarget as HTMLButtonElement | null
    if (button === null) return
    const service = button.dataset.service
    if (service === undefined) return

    this.serviceInputTarget.value = service
    requestAnimationFrame(() => this.form?.submit())
  }

  #buildUrl(key: keyof typeof keyMapping, value: string | [string, string]): URL {
    const urlObj = new URL(this.urlsValue[key])
    if (key === 'by_shortcode_ehub') {
      keyMapping.by_shortcode_ehub.forEach((key, index) => {
        urlObj.searchParams.set(key, value[index])
      })
    } else {
      urlObj.searchParams.set(keyMapping[key], value as string)
    }
    urlObj.searchParams.set('service', this.serviceValue)
    return urlObj
  }

  async #fetchParcels(
    fieldValue: string | [string, string],
    mode: keyof typeof keyMapping,
    buttonTarget: HTMLButtonElement,
    infoTarget: HTMLDivElement
  ): Promise<void> {
    if (fieldValue.length > 4 || Array.isArray(fieldValue)) {
      infoTarget.innerHTML = '... loading ...'
      const debouncedFunc = debounce(
        async () => {
          const response = await window.fetch(this.#buildUrl(mode, fieldValue))
          try {
            if (mode === 'by_parcel') {
              const data = (await response.json()) as { parcels: Parcel[] }
              const firstParcelWaybill = data.parcels.length > 0 ? data.parcels[0].waybill : null
              if (firstParcelWaybill === null) {
                buttonTarget.disabled = true
                infoTarget.innerHTML = 'No parcel found to add'
                return
              }
              this.parcelsToAdd = {
                parcels: data.parcels,
                processingEhubKey: firstParcelWaybill.processing_ehub_key,
                carrierRef: firstParcelWaybill.carrier_ref,
                firstArrivedAt: firstParcelWaybill.first_arrived_at,
                toParam: firstParcelWaybill.to_param
              }
            } else if (mode === 'by_waybill') {
              const data = (await response.json()) as { waybill: Record<string, unknown> }
              if (data.waybill === undefined) {
                buttonTarget.disabled = true
                infoTarget.innerHTML = 'No parcel found to add'
                return
              }
              this.parcelsToAdd = {
                parcels: data.waybill.regular_and_bulk_parcels as Parcel[],
                processingEhubKey: data.waybill.processing_ehub_key as string,
                carrierRef: data.waybill.carrier_ref as string,
                firstArrivedAt: data.waybill.first_arrived_at as string,
                toParam: data.waybill.to_param as string
              }
            } else if (mode === 'by_shortcode_ehub') {
              const data = (await response.json()) as { shortcode_ehub: { parcels: Parcel[] } }
              const parcels = data.shortcode_ehub.parcels
              if (parcels.length === 0) {
                buttonTarget.disabled = true
                infoTarget.innerHTML = 'No parcel found to add'
                return
              }
              const firstParcelWaybill = parcels[0].waybill
              this.parcelsToAdd = {
                parcels,
                processingEhubKey: firstParcelWaybill.processing_ehub_key,
                carrierRef: firstParcelWaybill.carrier_ref,
                firstArrivedAt: firstParcelWaybill.first_arrived_at,
                toParam: firstParcelWaybill.to_param
              }
            }
            const { repeatedIds, parcelIdsToAdd } = this.#checkRepeatedParcelsToAdd(
              this.parcelsToAdd?.parcels
            )
            this.repeatedIdsToAdd = repeatedIds
            const howManyParcels = parcelIdsToAdd.size ?? 0
            buttonTarget.disabled = howManyParcels === 0
            let infoTargetText = '<div class="mb-1 font-bold">'
            if (howManyParcels === 1) {
              infoTargetText += `1 parcel to add (${this.parcelsToAdd?.parcels[0].ref})`
            } else if (howManyParcels > 1) {
              infoTargetText += `${howManyParcels} parcels to add`
            } else if (howManyParcels === 0) {
              infoTargetText += 'No parcel found to add'
            }
            infoTargetText += '</div>'
            if (repeatedIds.size > 0) {
              infoTargetText += `<div class="text-sm text-red">${repeatedIds.size}
                parcel${repeatedIds.size > 1 ? 's' : ''} already on the list</div>`
            }
            infoTarget.innerHTML = infoTargetText
          } catch (error) {
            console.error('Error fetching parcel data:', error)
          }
        },
        mode === 'by_shortcode_ehub' ? 10 : 500
      )
      await debouncedFunc()
    } else {
      buttonTarget.disabled = true
      infoTarget.innerHTML = ''
    }
  }

  inputParcelToAdd(e: InputEvent): void {
    const value = (e.currentTarget as HTMLInputElement).value
    void this.#fetchParcels(
      value,
      'by_parcel',
      this.addParcelButtonTarget,
      this.addParcelInfoTarget
    )
  }

  inputWaybillToAdd(e: InputEvent): void {
    const value = (e.currentTarget as HTMLInputElement).value
    void this.#fetchParcels(
      value,
      'by_waybill',
      this.addWaybillButtonTarget,
      this.addWaybillInfoTarget
    )
  }

  #fetchShortcodeEhubParcels(): void {
    void this.#fetchParcels(
      [this.selectedShortcode, this.selectedEhub],
      'by_shortcode_ehub',
      this.addShortcodeEhubButtonTarget,
      this.addShortcodeEhubInfoTarget
    )
  }

  inputShortcodeToAdd(e: InputEvent): void {
    const value = (e.currentTarget as HTMLInputElement).value
    this.selectedShortcode = value
    this.#fetchShortcodeEhubParcels()
  }

  inputEHubToAdd(e: InputEvent): void {
    const value = (e.currentTarget as HTMLInputElement).value
    this.selectedEhub = value
    this.#fetchShortcodeEhubParcels()
  }

  #checkRepeatedParcelsToAdd(parcelsToAdd: ParcelInfoToAdd['parcels'] = []): {
    repeatedIds: Set<number>
    parcelIdsToAdd: Set<number>
  } {
    const table = this.element.querySelector('table')
    const rows = table?.querySelectorAll<HTMLTableRowElement>('tbody tr') ?? []
    const parcelIdsToAdd = new Set(parcelsToAdd.map(p => p.id))
    const repeatedIds = new Set<number>()
    for (const row of rows) {
      if (parcelIdsToAdd.size === 0) break
      const numberValue = parseInt(
        row.querySelector<HTMLInputElement>('input[type=checkbox]')?.value ?? '0'
      )
      const doesThisRowAlreadyHasTheIdToAdd = parcelIdsToAdd.has(numberValue)
      if (doesThisRowAlreadyHasTheIdToAdd) {
        repeatedIds.add(numberValue)
        parcelIdsToAdd.delete(numberValue)
      }
    }
    return { repeatedIds, parcelIdsToAdd }
  }

  // ADD parcels to the table

  addParcels(): void {
    const table = this.element.querySelector('table')

    const firstRow = table?.querySelectorAll('tbody tr')?.[0] as HTMLTableRowElement
    if (this.parcelsToAdd === null) return

    this.parcelsToAdd.parcels.forEach(parcel => {
      // detect whether or not we have this parcel already there at the table. If yes, skip it
      if (this.repeatedIdsToAdd.has(parcel.id)) return

      const newRow = firstRow?.cloneNode(true) as HTMLTableRowElement
      const newCells = newRow.querySelectorAll('td')

      // column 0: checkbox
      const checkbox = newCells[0].querySelector('input[type=checkbox]') as HTMLInputElement
      checkbox.value = String(parcel.id)
      // column 1: parcel ref link
      const parcelRefLink = newCells[1].querySelector('a') as HTMLAnchorElement
      updateLink(parcelRefLink, parcel.ref)
      // column 2: parcel type
      const parcelType = newCells[2].querySelector('div') as HTMLDivElement
      parcelType.innerText = capitalizedText(parcel.type)
      // column 3: waybill link
      const waybillLink = newCells[3].querySelector('a') as HTMLAnchorElement
      updateLink(
        waybillLink,
        this.parcelsToAdd?.carrierRef ?? '',
        this.parcelsToAdd?.toParam ?? '',
        'waybills'
      )
      // column 4: arrived time
      const arrivedTime = newCells[4].firstElementChild as HTMLDivElement
      updateTime(arrivedTime, this.parcelsToAdd?.firstArrivedAt ?? '')
      // column 5: client ref
      const clientRef = newCells[5].querySelector('div') as HTMLDivElement
      clientRef.innerText = parcel.client_ref
      // column 6: ehub
      const eHub = newCells[6].querySelector('div') as HTMLDivElement
      eHub.innerText = this.parcelsToAdd?.processingEhubKey ?? ''
      // column 7: shortcode
      const shortcode = newCells[7].querySelector('div') as HTMLDivElement
      shortcode.innerText = parcel.most_recent_delivery.shortcode
      // column 8: last scan time
      const lastScanTime = newCells[8].firstElementChild as HTMLDivElement
      updateTime(lastScanTime, parcel.most_recent_ehub_scan_at)

      // insert new row
      const tbody = table?.querySelector('tbody') as HTMLTableSectionElement
      tbody.insertBefore(newRow, firstRow)
    })

    // we can't know which modal is shown, so we just close all
    this.element.querySelectorAll('ui-modal').forEach(modalRaw => {
      const modal = modalRaw as HTMLDivElement
      modal.hidePopover()
    })
  }
}
